import {
  Info, CheckCircle, AlertTriangle, Star, TrendingUp,
  Target, Lightbulb, Zap, Flag, MessageSquare,
  Heart, Award, Bookmark, Eye, ArrowRight, Quote,
  type LucideIcon,
} from 'lucide-react'
import type { WidgetDisplay } from '../schemas'

/**
 * Source unique des presets de style de conteneur (WidgetShell).
 *
 * Registre « rapport » : fonds PASTEL doux + bordure 1px assortie, pensés pour un
 * rapport client. On a délibérément abandonné les callouts « alert-box » (aplat
 * saturé + bordure gauche épaisse + icône lucide imposée) : c'est le pattern
 * générique d'app web / IA, hors sujet sur un livrable éditorial.
 *
 * Le badge (texte) est rendu par WidgetShell en « chip éditorial » teinté avec
 * `chip_bg`/`chip_text` du preset. Aucune icône n'est imposée par un preset — elle
 * reste dispo en réglage manuel (`display.icon`), désactivée par défaut.
 */
export type StylePreset = {
  key: string
  label: string
  /** Fond pastel. `undefined` pour « Neutre » (cadre blanc par défaut). */
  bg?: string
  /** Bordure 1px assortie au fond. `undefined` pour « Neutre ». */
  border_color?: string
  /** Fond du chip de badge (teinte plus soutenue que le fond). */
  chip_bg?: string
  /** Texte du chip de badge (teinte foncée lisible). */
  chip_text?: string
}

export const STYLE_PRESETS: StylePreset[] = [
  { key: 'plain', label: 'Neutre' },
  { key: 'discret', label: 'Discret', bg: '#F7F8FB', border_color: '#ECEEF4', chip_bg: '#EEF0F5', chip_text: '#6B7280' },
  { key: 'bleu', label: 'Bleu', bg: '#EEF3FE', border_color: '#DBE4F8', chip_bg: '#E2EAFB', chip_text: '#3B5BB5' },
  { key: 'vert', label: 'Vert', bg: '#EDF9F1', border_color: '#D6EDDF', chip_bg: '#DCF0E4', chip_text: '#2F7D52' },
  { key: 'ambre', label: 'Ambre', bg: '#FBF5EA', border_color: '#EFE2CB', chip_bg: '#F2E6CE', chip_text: '#9A6B1E' },
  { key: 'violet', label: 'Violet', bg: '#F4F1FD', border_color: '#E4DCF7', chip_bg: '#EAE2FB', chip_text: '#6B4FB0' },
  { key: 'rose', label: 'Rose', bg: '#FCF0F3', border_color: '#F4DDE3', chip_bg: '#F7E0E6', chip_text: '#B0466A' },
]

/** Map des icônes lucide utilisables en réglage manuel (StyleTab › Apparence). */
export const ICON_MAP: Record<string, LucideIcon> = {
  Info, CheckCircle, AlertTriangle, Star, TrendingUp,
  Target, Lightbulb, Zap, Flag, MessageSquare,
  Heart, Award, Bookmark, Eye, ArrowRight, Quote,
}

/**
 * Renvoie le patch de `display` pour un preset donné.
 *
 * « Neutre » (`plain`) remet toutes les clés de style à `undefined` pour retomber
 * sur le cadre par défaut de WidgetShell (blanc, bordure #E2E4ED). Les presets
 * pastel posent `bg` + `border: 'full'` (1px) + `border_color`. Le texte du badge
 * (`display.badge`) n'est PAS touché : c'est du contenu utilisateur. Preset inconnu
 * → fallback « Neutre ».
 */
export function applyPreset(key: string): { display: Partial<WidgetDisplay> } {
  const preset = STYLE_PRESETS.find((p) => p.key === key) ?? STYLE_PRESETS[0]
  if (preset.key === 'plain') {
    return {
      display: {
        preset: 'plain',
        bg: undefined,
        border: undefined,
        border_color: undefined,
        shadow: undefined,
        icon: undefined,
        icon_color: undefined,
      },
    }
  }
  return {
    display: {
      preset: preset.key,
      bg: preset.bg,
      border: 'full',
      border_color: preset.border_color,
      shadow: false,
      icon: undefined,
      icon_color: undefined,
    },
  }
}
